import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api, getSessionEpoch } from './api';
import { navigationCache, navigationCacheEnabled, cacheable } from './navigation-cache';

/** Загрузка данных экрана: один запрос, состояние и перезагрузка. */
export function useApi<T>(path: string | null): {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
  setData: (next: T) => void;
} {
  const cached = !!path && navigationCacheEnabled && cacheable(path);
  const epoch = getSessionEpoch();
  const initial = cached ? navigationCache.peek<T>(path!) : null;
  const [state, setState] = useState({ path, epoch, data: initial, error: null as string | null, loading: !!path && !initial });
  const runRef = useRef<(force?: boolean) => void>(() => {});
  useEffect(() => {
    let sequence = 0;
    let disposed = false;
    const controllers = new Set<AbortController>();
    const run = (force = false) => {
      const seq = ++sequence;
      if (!path) { setState({path,epoch,data:null,error:null,loading:false}); return; }
      const snapshot = cached ? navigationCache.peek<T>(path) : null;
      setState(previous => ({path,epoch,data:snapshot ?? (!cached && previous.path===path ? previous.data : null),error:null,loading:!snapshot}));
      const controller = new AbortController(); controllers.add(controller);
      api.get<T>(path, controller.signal, force).then(data => {
        if (!disposed && seq===sequence && epoch===getSessionEpoch()) setState({path,epoch,data,error:null,loading:false});
      }).catch((err: unknown) => {
        if (!disposed && seq===sequence && !controller.signal.aborted && epoch===getSessionEpoch())
          setState(previous=>({...previous,error:err instanceof ApiError || err instanceof Error ? err.message : 'Не удалось загрузить данные',loading:false}));
      }).finally(()=>controllers.delete(controller));
    };
    runRef.current=run; run();
    const refresh = () => { if(document.visibilityState==='visible') run(); };
    const unsubscribe = cached ? navigationCache.subscribe(()=>run()) : () => {};
    if(cached) { window.addEventListener('focus',refresh); document.addEventListener('visibilitychange',refresh); }
    const timer = cached ? window.setInterval(refresh,30_000) : undefined;
    return () => { disposed=true; for(const controller of controllers)controller.abort(); unsubscribe(); window.clearInterval(timer); window.removeEventListener('focus',refresh); document.removeEventListener('visibilitychange',refresh); };
  }, [path, epoch, cached]);
  const reload = useCallback(() => runRef.current(true), []);
  const setData = useCallback((data: T) => setState(previous=>({...previous,data})), []);
  const current = state.path===path && state.epoch===epoch;
  return { data: current ? state.data : initial, error: current ? state.error : null, loading: current ? state.loading : !!path && !initial, reload, setData };
}

/** Задержка ввода в поиске. */
export function useDebounced<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/**
 * Автосохранение черновика: копит правки и отправляет пачкой.
 * Черновик тренировки сохраняется сам (SPEC §5 T1–T3).
 */
export function useAutosave<T>(
  save: (value: T) => Promise<void>,
  delay = 900,
): { schedule: (value: T) => void; flush: () => Promise<void>; saving: boolean } {
  const [saving, setSaving] = useState(false);
  // Capture the writer with its value, never retarget queued data after a render.
  const pending = useRef<{ value: T; save: typeof save } | null>(null);
  const active = useRef<Promise<void> | null>(null);
  const mounted = useRef(true);
  const timer = useRef<number | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;

  const run = useCallback((): Promise<void> => {
    if (!mounted.current) return Promise.reject(new Error('Автосохранение отменено'));
    if (active.current) return active.current;
    if (!pending.current) return Promise.resolve();
    setSaving(true);
    // Install the shared promise before invoking any writer. One drain owns all writes.
    const request = Promise.resolve().then(async () => {
      while (pending.current && mounted.current) {
        const item = pending.current;
        pending.current = null;
        try {
          await item.save(item.value);
        } catch (error) {
          // Keep the newest draft for an explicit retry, never spin on a failed write.
          if (mounted.current && !pending.current) pending.current = item;
          if (timer.current !== null) window.clearTimeout(timer.current);
          timer.current = null;
          throw error;
        }
      }
      if (!mounted.current) throw new Error('Автосохранение отменено');
    }).finally(() => {
      active.current = null;
      if (mounted.current) setSaving(false);
    });
    active.current = request;
    return request;
  }, []);

  const schedule = useCallback(
    (value: T) => {
      if (!mounted.current) return;
      pending.current = { value, save: saveRef.current };
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        timer.current = null;
        // Explicit flush callers still observe rejection; background failure stays dirty.
        void run().catch(() => {});
      }, delay);
    },
    [delay, run],
  );

  const flush = useCallback(async () => {
    if (timer.current) window.clearTimeout(timer.current);
    await run();
  }, [run]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      pending.current = null;
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = null;
    };
  }, []);

  return { schedule, flush, saving };
}
